import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Lock, CheckCircle2 } from "lucide-react";

export default function RecipientUpload() {
  const { token } = useParams();
  const [lead, setLead] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/programs/recipients/${token}`).then((r) => {
      setLead(r.data);
      if (r.data.submitted) setDone(true);
    }).catch(() => setError("This link is invalid or has expired."));
  }, [token]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setText((prev) => (prev ? prev + "\n" : "") + String(reader.result || ""));
    reader.readAsText(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/programs/recipients/${token}`, { raw_text: text });
      setDone(true);
      toast.success(`Received ${data.count} recipient(s) — thank you 💛`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save your list.");
    } finally { setBusy(false); }
  };

  if (error) return <div className="max-w-xl mx-auto px-5 py-24 text-center text-[#6B655F]" data-testid="recipient-error">{error}</div>;
  if (!lead) return null;

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-16">
      <div className="text-center">
        <div className="h-12 w-12 rounded-full bg-[#8E9A86]/12 mx-auto flex items-center justify-center mb-3"><Lock className="h-6 w-6 text-[#8E9A86]" /></div>
        <span className="gh-overline">Secure recipient list</span>
        <h1 className="font-heading text-4xl text-[#3A3632] mt-3">{lead.program_title}</h1>
        <p className="text-[#6B655F] mt-3 leading-relaxed">For <strong>{lead.company}</strong> · {lead.quantity} boxes. Add the names and addresses of your recipients below. This information is handled securely and privately.</p>
      </div>

      {done ? (
        <div className="mt-10 bg-[#FAF7F2] border border-[#D9CBA0] rounded-2xl p-8 text-center" data-testid="recipient-done">
          <CheckCircle2 className="h-10 w-10 text-[#8E9A86] mx-auto mb-3" />
          <p className="text-[#3A3632] text-lg">Your recipient list has been received.</p>
          <p className="text-sm text-[#8A8175] mt-2">Thank you — we'll prepare each box with care. You can revisit this link anytime to update your list.</p>
          <button onClick={() => setDone(false)} className="text-sm text-[#8E9A86] underline mt-4" data-testid="recipient-edit">Update the list</button>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-10 bg-white border border-[#E5DFD5] rounded-2xl p-8" data-testid="recipient-form">
          <label className="block">
            <span className="text-sm text-[#3A3632] font-medium mb-1.5 block">Recipient list</span>
            <span className="text-xs text-[#8A8175] mb-2 block">One recipient per line, e.g. <em>Jane Doe, 123 Main St, Apt 2, Springfield, IL 62704</em></span>
            <textarea rows={12} value={text} onChange={(e) => setText(e.target.value)} required data-testid="recipient-textarea"
              className="w-full border border-[#D1CCC5] rounded-lg px-4 py-3 outline-none focus:border-[#8E9A86] focus:ring-2 focus:ring-[#8E9A86]/30 resize-none font-mono text-sm" />
          </label>
          <div className="mt-3 text-sm text-[#6B655F]">
            Or upload a CSV/text file:{" "}
            <input type="file" accept=".csv,.txt" onChange={onFile} data-testid="recipient-file" className="text-sm" />
          </div>
          <button type="submit" disabled={busy} data-testid="recipient-submit"
            className="w-full mt-5 bg-[#8E9A86] text-white rounded-full px-6 py-3.5 font-medium hover:bg-[#7A8772] transition-colors disabled:opacity-60">
            {busy ? "Saving securely…" : "Submit recipient list"}
          </button>
          <p className="text-xs text-[#8A8175] text-center mt-3">🔒 Your recipients' details are stored securely and used only to fulfill this order.</p>
        </form>
      )}
    </div>
  );
}
